import React, { useCallback, useEffect } from 'react'
import { IRootReducer, Memecoin } from '../../../../types/reducerTypes'
import AddComment from './AddComment'
import { useDispatch, useSelector } from 'react-redux'
import { useLazyGetMemecoinCommentsByAddressQuery } from '../../../../modules/memecoins/api'
import {
  appendMemecoinComment,
  appendMemecoinCommentReaction,
  appendMemecoinCommentReply,
  setMemecoinComments,
} from '../../../../modules/memecoins/slice'
import { selectMemecoinComments } from '../../../../modules/memecoins/selectors'
import MemecoinThreadList from './ThreadList'
import AnimatedLoader from '../../../../shared_components/AnimatedLoader'
import {
  channels,
  useWebSocketContext,
} from '../../../../contexts/WebSocketContext'
import { Box } from '../../../../shared_components/UI/Box'
import styled from 'styled-components'
import { pagePaddingCssRules } from '../../../../shared_components/Header/styles'

function MemecoinThreads({ memecoin }: { memecoin: Memecoin }) {
  const poolAddress = memecoin.poolAddress
  const dispatch = useDispatch()
  const [fetchMemecoinComments, { isLoading }] =
    useLazyGetMemecoinCommentsByAddressQuery()
  const { subscribe, unsubscribe } = useWebSocketContext()
  const comments = useSelector((state: IRootReducer) =>
    selectMemecoinComments(state),
  )

  const handleFetchMemecoinComments = useCallback(async () => {
    try {
      const { comments } = await fetchMemecoinComments(
        {
          address: memecoin.poolAddress,
        },
        true,
      ).unwrap()
      dispatch(setMemecoinComments(comments))
    } catch (e) {
      console.error('Error fetching memecoin comments:', e)
    }
  }, [fetchMemecoinComments, poolAddress, dispatch])

  useEffect(() => {
    const channel = channels.commentCreated(poolAddress)

    subscribe('threads-table', channel, (message) => {
      dispatch(appendMemecoinComment(message.data))
    })

    return () => {
      unsubscribe('threads-table', channel)
    }
  }, [dispatch, poolAddress, subscribe, unsubscribe])

  useEffect(() => {
    const channel = channels.commentUpdated(poolAddress)

    subscribe('threads-table', channel, (message) => {
      const replies = message.data.replies
      const reactions = message.data.reactions
      dispatch(
        appendMemecoinCommentReply({ replies, commentId: message.data._id }),
      )
      dispatch(
        appendMemecoinCommentReaction({
          reactions,
          commentId: message.data._id,
        }),
      )
    })

    return () => {
      unsubscribe('threads-table', channel)
    }
  }, [dispatch, poolAddress, subscribe, unsubscribe])

  useEffect(() => {
    handleFetchMemecoinComments()
  }, [handleFetchMemecoinComments])

  if (isLoading) {
    return <AnimatedLoader onlyWhite size={24} />
  }

  return (
    <Box dc>
      <MemecoinThreadList comments={comments} />
      <AddComment memecoin={memecoin} />
    </Box>
  )
}

export default MemecoinThreads
